/**
 * Encode mono Float32Array PCM samples to a 16-bit PCM WAV Blob.
 *
 * The cluster's Symphonia decoder reads RIFF/WAV trivially. Producing this
 * format here means the upload always has a recognizable header, so the
 * "saved as .bin → fall through to MP3 demuxer → fail" pitfall can't bite.
 */
export function encodeWav(samples: Float32Array, sampleRate = 16000): Blob {
  const len = samples.length;
  const buf = new ArrayBuffer(44 + len * 2);
  const view = new DataView(buf);

  // RIFF chunk header
  writeAscii(view, 0, "RIFF");
  view.setUint32(4, 36 + len * 2, true);
  writeAscii(view, 8, "WAVE");

  // fmt sub-chunk
  writeAscii(view, 12, "fmt ");
  view.setUint32(16, 16, true); // sub-chunk size
  view.setUint16(20, 1, true); // PCM
  view.setUint16(22, 1, true); // mono
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true); // byte rate
  view.setUint16(32, 2, true); // block align
  view.setUint16(34, 16, true); // bits per sample

  // data sub-chunk
  writeAscii(view, 36, "data");
  view.setUint32(40, len * 2, true);
  for (let i = 0; i < len; i++) {
    const s = clamp(samples[i] ?? 0, -1, 1);
    view.setInt16(44 + i * 2, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }
  return new Blob([buf], { type: "audio/wav" });
}

function writeAscii(view: DataView, offset: number, s: string): void {
  for (let i = 0; i < s.length; i++) {
    view.setUint8(offset + i, s.charCodeAt(i));
  }
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}
